import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { X, Pencil, Forward } from 'lucide-react'
import { useChangeTaskActionType } from '../hooks'
import type { Task } from '../types'
import { ActionTypeChip } from './ActionTypeChip'
import { ActionTypeSelect } from './ActionTypeSelect'
import { AssigneeChip } from './AssigneeChip'
import { ChangeAssigneeDialog } from './ChangeAssigneeDialog'
import { ChangeContextDialog } from './ChangeContextDialog'
import { CommentThread } from './CommentThread'
import { ContextLink } from './ContextLink'
import { RichTextReadView } from '@/features/contract/components/field-renderer/read/RichTextReadView'
import { FilesSection } from './files/FilesSection'
import { PriorityBadge } from './PriorityBadge'
import { TaskStatusActions } from './TaskStatusActions'

interface TaskDetailDrawerProps {
  task: Task | null
  onClose: () => void
}

const STATUS_LABELS: Record<string, string> = {
  NEW: 'Nowe',
  IN_PROGRESS: 'W toku',
  DONE: 'Gotowe',
  CANCELLED: 'Anulowane',
}

/**
 * Sheet z prawej strony - klik karty kanban / wiersza listy otwiera. Pokazuje pełne
 * detale taska (read-only edycja pól = follow-up) + wątek komentarzy. Pierwsza
 * infrastruktura "kliknij kartę = zobacz szczegóły" dla przyszłych rozszerzeń
 * (edycja pól, audit log per task, attachments).
 */
export function TaskDetailDrawer({ task, onClose }: TaskDetailDrawerProps) {
  const changeActionType = useChangeTaskActionType()
  const [contextDialogOpen, setContextDialogOpen] = useState(false)
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState(false)
  if (!task) return null
  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content className="fixed right-0 top-0 bottom-0 z-50 w-[480px] max-w-full bg-white shadow-xl flex flex-col">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-start justify-between gap-2 mb-2">
              <Dialog.Title className="text-base font-semibold flex-1">{task.title}</Dialog.Title>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded hover:bg-slate-100 text-slate-500"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <PriorityBadge priority={task.priority} />
              <span className="px-2 py-0.5 rounded bg-slate-100">
                {STATUS_LABELS[task.status] ?? task.status}
              </span>
              {task.dueDate && <span className="text-slate-500">📅 {task.dueDate}</span>}
              {task.actionTypeCode && task.actionTypeLabel && (
                <ActionTypeChip code={task.actionTypeCode} label={task.actionTypeLabel} />
              )}
              <div className="flex items-center gap-1">
                <AssigneeChip assignee={task.assignee} claimedBy={task.claimedBy} />
                <button
                  type="button"
                  onClick={() => setAssigneeDialogOpen(true)}
                  className="flex size-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-sky-700"
                  title="Przepnij do innej osoby/zespołu"
                  aria-label="Przepnij przypisanie"
                >
                  <Forward className="size-3" />
                </button>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-slate-500 shrink-0">Typ akcji:</span>
              <ActionTypeSelect
                value={task.actionTypeCode}
                onChange={(code) => changeActionType.mutate({ taskId: task.id, actionTypeCode: code })}
                className="w-auto"
              />
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              {task.context ? (
                <>
                  <span className="text-slate-500 shrink-0">Kontekst:</span>
                  <ContextLink context={task.context} className="font-medium" />
                  <button
                    type="button"
                    onClick={() => setContextDialogOpen(true)}
                    className="flex size-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-sky-700 shrink-0"
                    title="Zmień kontekst"
                    aria-label="Zmień kontekst"
                  >
                    <Pencil className="size-3" />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setContextDialogOpen(true)}
                  className="text-slate-400 hover:text-slate-600 border border-dashed border-slate-300 rounded px-2 py-0.5 hover:border-slate-400"
                >
                  + Przypisz kontekst
                </button>
              )}
            </div>
            <div className="mt-3">
              <TaskStatusActions task={task} />
            </div>
          </div>
          {contextDialogOpen && (
            <ChangeContextDialog task={task} onClose={() => setContextDialogOpen(false)} />
          )}
          {assigneeDialogOpen && (
            <ChangeAssigneeDialog task={task} onClose={() => setAssigneeDialogOpen(false)} />
          )}
          {task.description && (
            <div className="p-4 border-b border-slate-200">
              <RichTextReadView value={task.description} />
            </div>
          )}
          <div className="p-4 border-b border-slate-200">
            <FilesSection taskId={task.id} />
          </div>
          <div className="flex-1 min-h-0 p-4 overflow-y-auto">
            <CommentThread taskId={task.id} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
